import React from "react";
import type { ApolloClient } from "@apollo/client";
// react-rangeslider ships no types and @types/react-rangeslider is not
// installed; it resolves to an untyped JS module (so it cannot be augmented or
// shimmed locally). The slider callbacks below are annotated explicitly.
// @ts-expect-error untyped third-party module — no declaration available
import Slider from "react-rangeslider";
import { Formik } from "formik";
import type { FormikErrors } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { find, get, isNil, last, map, replace, noop, isEmpty } from "lodash";
import { INVESTMENT_PROFILE } from "statics/onboardingSteps";
import { getAllAccounts } from "actions/accountActions";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Carousel, Col, Form, Row } from "react-bootstrap";
import { userService } from "services/userService";
import { ScrollToFieldError } from "utils/form";
import { formatCurrency } from "utils/number";
import { userConstants, accountConstants } from "actions/types";
import { scrollToTop } from "utils/dom";
import {
  accountTypesToEnglishMappingCAPS,
  accountTypesToEnglishMapping,
} from "statics/accountTypes";
import {
  getIraAccountIdSelector,
  isOnboardingSubsequentAccountSelector,
  isRolloverSelector,
} from "store/selectors/user";
import {
  getInvestmentProfileAndSuitability,
  getRequiredUpdates,
  getUserState,
  startPortfolioUpdate,
  updateInvestmentProfileAndSuitability,
  updateRolloverAccountTermsAndSubmit,
  updateIraAccountInvestmentProfile,
  updateIraAccountSuitability,
  updateRolloverInvestmentProfileAndSuitability,
} from "actions/userActions";
import {
  experienceValues,
  hideLiquidInvestment,
  incomeLabel,
  incomeLimits,
  oldTimeHorizonLiquidityValues,
  riskToleranceValues,
  timeHorizonLiquidityValues,
  wealthLabel,
  wealthLimits,
} from "utils/investProfileHelper";
import {
  IndividualInvestmentProfileUpdate,
  IndividualRequiredUpdatesPending,
} from "statics/states";

import ProgressBar from "components/ProgressBar";
import CircularProgressBar from "components/CircularProgressBar";
import SimpleModal from "components/SimpleModal";
import IconSpinner from "components/IconSpinner";
import Button from "components/Button";
import Alert from "components/Alert";
import SelectBox from "./SelectBox";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import IconInfoCard from "components/IconInfoCard";
import CancelRolloverModal from "../../dashboards/individualDashboard/dashboard/CancelRolloverModal";
import { investmentAccountsSelector } from "store/selectors/accounts";
import type { RootState } from "types/state";
import type { GetInvestmentProfileAndSuitabilityQuery } from "types/graphql";

import "react-datepicker/dist/react-datepicker.css";
import "react-rangeslider/lib/index.css";

import "./IndividualInvestmentProfile.scss";

interface InvestmentProfileQuestionsProps {
  investmentProfileQuestion?: string;
  title?: string;
  subtext?: string;
}

const InvestmentProfileQuestions = ({
  investmentProfileQuestion,
  title,
  subtext,
}: InvestmentProfileQuestionsProps) => {
  return (
    <section className="page-title-wrap">
      <article className="text-cell">
        <p className="page-eyebrow">{title}</p>
        <p className="page-title">{investmentProfileQuestion}</p>
        {subtext && <p className="page-subtext">{subtext}</p>}
      </article>
    </section>
  );
};

const suitabilityQuestions = [
  "incomeLimitsRawValue",
  "wealthLimitsSelectedIndex",
  "riskTolerance",
  "experience",
  "timeHorizonLiquidity",
];

const suitabilityQuestionswithLiquid = [
  "incomeLimitsRawValue",
  "wealthLimitsSelectedIndex",
  "liquidNetWorthLimitsSelectedIndex",
  "riskTolerance",
  "experience",
  "timeHorizonLiquidity",
];
const maxIncomeLimit = 1000000000;
import * as yup from "yup";

const schema = yup.object({
  experience: yup.string().label("Experience").required(),
  incomeLimitsRawValue: yup
    .number()
    .label("Annual Income")
    .required()
    .min(0)
    .max(maxIncomeLimit),
  liquidNetWorthLimitsSelectedIndex: yup
    .string()
    .when("totalNetWorth", {
      is: (val: string | undefined) => {
        return !hideLiquidInvestment(val);
      },
      then: (s) => s.required(),
      otherwise: (s) => s,
    })
    .label("Liquid Net Worth"),
  wealthLimitsSelectedIndex: yup.string().label("Total Net Worth").required(),
  riskTolerance: yup.string().label("Risk Tolerance").required(),
  timeHorizonLiquidity: yup.string().label("Time Horizon Liquidity").required(),
});

const actions = [
  userConstants.USER_ON_BOARDING_INVESTMENT_SUITABILITY_UPDATE,
  userConstants.USER_ON_BOARDING_INVESTMENT_PROFILE_UPDATE,
  userConstants.USER_ROLLOVER_INVESTMENT_PROFILE_UPDATE,
  userConstants.USER_INVESTMENT_PROFILE_UPDATE,
  userConstants.GET_USER_REQUIRED_STEPS,
  accountConstants.GET_ACCOUNTS,
];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  // selectors/user.js userState slice is `object` in RootState — the legacy
  // `.state` field is read directly here, so narrow at the boundary.
  const userState = (state.user.userState as { state: string }).state;

  const requiredUpdates = get(state, "user.requiredUpdates.updates", []);

  const accounts = investmentAccountsSelector(state);

  const multiAccountRequiredUpdates = accounts.length > 1;
  return {
    userState,
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    fallbackAccountId: getIraAccountIdSelector(state),
    accounts,
    isUpdatingInvestmentProfile:
      IndividualInvestmentProfileUpdate === userState,
    isInRequiredStepFlow: IndividualRequiredUpdatesPending === userState,
    isRolloverAccount: isRolloverSelector(state),
    isSubsequentAccount: isOnboardingSubsequentAccountSelector(state),
    requiredUpdates,
    multiAccountRequiredUpdates,
  };
};

const mapDispatchToProps = {
  updateIraAccountInvestmentProfile,
  updateIraAccountSuitability,
  getUserState,
  updateInvestmentProfileAndSuitability,
  updateRolloverAccountTermsAndSubmit,
  startPortfolioUpdate,
  getAllAccounts,
  getInvestmentProfileAndSuitability,
  getRequiredUpdates,
  updateRolloverInvestmentProfileAndSuitability,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  updatingAccountId?: string;
  accountType?: string;
}

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

/** A single required-update entry rendered in the modal. */
interface RequiredUpdate {
  accountType: string;
}

/**
 * Local shape of the IconInfoCard data built in render. Note `content` holds a
 * JSX element and `key` is numeric, which is wider than IconInfoCard's declared
 * section type — preserved verbatim; the prop is cast at the call site.
 */
interface InfoCard {
  title?: string;
  sections: Array<{
    key: number;
    title: string;
    content: React.ReactNode;
  }>;
}

/** Investment-profile portion of the onboarding form (Formik values). */
interface FormValues {
  objective: string;
  experience: string;
  incomeLimitsRawValue: number | null;
  wealthLimitsSelectedIndex: number;
  liquidNetWorthLimitsSelectedIndex: number;
  riskTolerance: string;
  timeHorizon: string;
  liquidityNeeds: string;
  timeHorizonLiquidity: string;
  incomeLimitsDisplayValue: string;
  // not seeded in initialValues, but the schema's `when("totalNetWorth")` and
  // the JSX `errors.totalNetWorth`/`errors.liquidNetWorth` reads reference
  // them; declared optional so those pre-existing references keep resolving.
  totalNetWorth?: string;
  liquidNetWorth?: string;
}

/** Prior-answer view of the fetched profile — fields become display strings. */
interface CurrentInvestmentProfile {
  annualIncome?: string;
  liquidNetWorth?: string;
  totalNetWorth?: string;
  experience?: string;
  formMetadata?: string;
  [key: string]: unknown;
}

interface CurrentSuitability {
  timeHorizonLiquidity?: string;
  [key: string]: unknown;
}

interface State {
  showCancelRolloverModal: boolean;
  currentQuestionIndex: number;
  investmentProfile: {
    objective: string;
    experience: string;
    incomeLimitsRawValue: number | null;
    wealthLimitsSelectedIndex: number;
    liquidNetWorthLimitsSelectedIndex: number;
    riskTolerance: string;
  };
  suitability: {
    timeHorizon: string;
    liquidityNeeds: string;
    timeHorizonLiquidity: string;
  };
  loading: boolean;
  submitting: boolean;
  submitted: boolean;
  showTextModal: boolean;
  currentInvestmentProfile: CurrentInvestmentProfile;
  currentSuitability: CurrentSuitability;
}

class IndividualInvestmentProfile extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      showCancelRolloverModal: false,
      currentQuestionIndex: 0,
      investmentProfile: {
        objective: "",
        experience: "",
        incomeLimitsRawValue: null,
        wealthLimitsSelectedIndex: 0,
        liquidNetWorthLimitsSelectedIndex: 0,
        riskTolerance: "",
      },
      suitability: {
        timeHorizon: "",
        liquidityNeeds: "",
        timeHorizonLiquidity: "",
      },
      loading: get(props, "isUpdatingInvestmentProfile", false),
      submitting: false,
      submitted: false,
      showTextModal: false,
      currentInvestmentProfile: {},
      currentSuitability: {},
    };
  }

  getEffectiveAccountId = () => {
    if (this.props.isInRequiredStepFlow && this.props.updatingAccountId) {
      return this.props.updatingAccountId;
    }
    return this.props.fallbackAccountId;
  };

  componentDidMount() {
    this._isMounted = true;
    window.analytics.page("Individual Investment Profile");
    scrollToTop();
    if (
      this.props.isUpdatingInvestmentProfile ||
      this.props.isInRequiredStepFlow
    ) {
      void this.props
        .getAllAccounts(this.props.client as ApolloClient<object>)
        .then(() =>
          this.props.getInvestmentProfileAndSuitability(
            this.props.client as ApolloClient<object>,
            this.getEffectiveAccountId() as string
          )
        )
        .then((data) => {
          const profileData = data as GetInvestmentProfileAndSuitabilityQuery;
          const investmentProfileInfo =
            data && profileData.getInvestmentProfile;
          const currentSuitability = data && profileData.getSuitability;
          let currentInvestmentProfile = {
            ...investmentProfileInfo,
            annualIncome: incomeLabel(
              investmentProfileInfo!.annualIncome as Parameters<
                typeof incomeLabel
              >[0]
            ),
            liquidNetWorth: wealthLabel(
              investmentProfileInfo!.liquidNetWorth as Parameters<
                typeof wealthLabel
              >[0]
            ),
            totalNetWorth: wealthLabel(
              investmentProfileInfo!.totalNetWorth as Parameters<
                typeof wealthLabel
              >[0]
            ),
            riskTolerance: investmentProfileInfo!.riskToleranceRawValue,
          } as CurrentInvestmentProfile;
          this.setState({
            currentInvestmentProfile,
            currentSuitability: currentSuitability as CurrentSuitability,
            showTextModal:
              this.props.userState === IndividualRequiredUpdatesPending,
            loading: false,
          });
        });
      return;
    }
    void userService
      .getIraAccountForm(this.props.client as ApolloClient<object>)
      .then(
        (data) => {
          let investmentProfile = {
            objective: data!.investmentProfile!.objective!,
            experience: data!.investmentProfile!.experience!,
            incomeLimitsRawValue: null,
            wealthLimitsSelectedIndex: 0,
            liquidNetWorthLimitsSelectedIndex: 0,
            riskTolerance: "",
          };

          let suitability = {
            timeHorizon: data!.suitability!.timeHorizon!,
            liquidityNeeds: data!.suitability!.liquidityNeeds!,
            timeHorizonLiquidity: data!.suitability!.timeHorizonLiquidity!,
          };

          if (this._isMounted) {
            this.setState({
              submitted: false,
              investmentProfile,
              suitability,
              loading: false,
            });
          }
        },
        () => {
          if (this._isMounted) {
            this.setState({
              submitted: false,
              loading: false,
            });
          }
        }
      );
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  submitInvestmentProfile = (values: FormValues) => {
    this.setState({
      submitting: true,
    });
    let metaData = {
      riskToleranceObjectSelection: riskToleranceValues.find(
        (item) => item.value === values.riskTolerance
      ),
      timeHorizonLiquiditySelection:
        timeHorizonLiquidityValues[
          values.timeHorizonLiquidity as keyof typeof timeHorizonLiquidityValues
        ],
      experienceSelection: experienceValues.find(
        (item) => item.value === values.experience
      ),
    };

    const maxIncomeRange = last(incomeLimits)!.max;
    let selectedIncomeLimits;

    if (values.incomeLimitsRawValue! > maxIncomeRange) {
      selectedIncomeLimits = last(incomeLimits);
    } else {
      selectedIncomeLimits = find(
        incomeLimits,
        (limit) =>
          limit.min <= values.incomeLimitsRawValue! &&
          values.incomeLimitsRawValue! <= limit.max
      );
    }

    const selectedWealthLimits = wealthLimits[values.wealthLimitsSelectedIndex];
    const selectedliquidNetWorthLimits =
      wealthLimits[values.liquidNetWorthLimitsSelectedIndex];

    let investmentProfile = {
      objective: "",
      experience: values.experience,
      annualIncome: {
        min: selectedIncomeLimits!.min,
        max: selectedIncomeLimits!.max,
        rawValue: +values.incomeLimitsRawValue!,
      },
      liquidNetWorth: {
        min: selectedliquidNetWorthLimits.min,
        max: selectedliquidNetWorthLimits.max,
      },
      totalNetWorth: {
        min: selectedWealthLimits.min,
        max: selectedWealthLimits.max,
      },
      riskTolerance: values.riskTolerance,
      riskToleranceRawValue: values.riskTolerance,
      formMetadata: JSON.stringify(metaData),
    };

    const timeHorizonLiquidityNeeds =
      timeHorizonLiquidityValues[
        values.timeHorizonLiquidity as keyof typeof timeHorizonLiquidityValues
      ];

    let suitability = {
      timeHorizon: timeHorizonLiquidityNeeds.timeHorizon,
      liquidityNeeds: timeHorizonLiquidityNeeds.liquidityNeeds,
      timeHorizonLiquidity: timeHorizonLiquidityNeeds.value,
    };

    // Risk Tolerance values also set Objective
    switch (values.riskTolerance) {
      case "LOW_CAP":
        investmentProfile.riskTolerance = "LOW";
        investmentProfile.objective = "CAPITAL_PRESERVATION";
        break;
      case "LOW_GROWTH":
        investmentProfile.riskTolerance = "LOW";
        investmentProfile.objective = "GROWTH";
        break;
      case "MEDIUM_GROWTH_1":
        investmentProfile.riskTolerance = "MEDIUM";
        investmentProfile.objective = "GROWTH";
        break;
      case "MEDIUM_GROWTH_2":
        investmentProfile.riskTolerance = "MEDIUM";
        investmentProfile.objective = "GROWTH";
        break;
      case "HIGH_GROWTH":
        investmentProfile.riskTolerance = "HIGH";
        investmentProfile.objective = "GROWTH";
        break;
    }

    const { accounts } = this.props;
    const accountId = this.getEffectiveAccountId();

    const activeAccount = isEmpty(accountId)
      ? get(accounts, "[0]")
      : find(accounts, { id: accountId });
    const accountState = get(activeAccount, "state");
    const isRollingOverNewAccount =
      !isEmpty(accountState) &&
      accountState === "IndividualDisclosuresComplete";

    if (
      (this.props.isRolloverAccount || this.props.isSubsequentAccount) &&
      isRollingOverNewAccount
    ) {
      void this.props
        .updateRolloverInvestmentProfileAndSuitability(
          this.props.client as ApolloClient<object>,
          investmentProfile as Parameters<
            Props["updateRolloverInvestmentProfileAndSuitability"]
          >[1],
          suitability as Parameters<
            Props["updateRolloverInvestmentProfileAndSuitability"]
          >[2]
        )
        .then(() => {
          if (this.props.isUpdatingInvestmentProfile) {
            return this.props.startPortfolioUpdate(
              this.props.client as ApolloClient<object>
            );
          } else if (this.props.isInRequiredStepFlow) {
            void this.props.getRequiredUpdates(
              this.props.client as ApolloClient<object>
            );
          }
        })
        .then(() => {
          if (!this.props.isUpdatingInvestmentProfile) {
            this.setState({ submitting: false });
          }
        });

      window.analytics.track("Submitted Rollover Account Investment Profile");
    } else {
      void this.props
        .updateInvestmentProfileAndSuitability(
          this.props.client as ApolloClient<object>,
          this.getEffectiveAccountId() as string,
          // the locally-built profile/suitability use plain strings where the
          // mutation input declares enums; cast at the boundary to preserve the
          // exact runtime payload.
          investmentProfile as Parameters<
            Props["updateInvestmentProfileAndSuitability"]
          >[2],
          suitability as Parameters<
            Props["updateInvestmentProfileAndSuitability"]
          >[3],
          !!this.props.updatingAccountId
        )
        .then(() => {
          if (this.props.isUpdatingInvestmentProfile) {
            return this.props.startPortfolioUpdate(
              this.props.client as ApolloClient<object>
            );
          } else if (this.props.isInRequiredStepFlow) {
            void this.props.getRequiredUpdates(
              this.props.client as ApolloClient<object>
            );
          }
          return this.props.getUserState(
            this.props.client as ApolloClient<object>
          );
        })
        .then(() => {
          if (!this.props.isUpdatingInvestmentProfile) {
            this.setState({ submitting: false });
          }
        });

      window.analytics.track("Submitted Account Investment Profile", {
        userState: this.props.userState,
      });
    }

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  _setSelectedQuestion = (currentQuestionIndex: number) => {
    this.setState({ currentQuestionIndex });
  };

  _advanceCarousel = () => {
    this.setState({
      currentQuestionIndex: this.state.currentQuestionIndex + 1,
    });
  };

  _isLastQuestion = (isWithLiquid: boolean) => {
    const setOfQuestions = isWithLiquid
      ? suitabilityQuestionswithLiquid
      : suitabilityQuestions;
    const indexIsLast =
      this.state.currentQuestionIndex === setOfQuestions.length - 1;
    return indexIsLast;
  };

  calcContinueBtnDisabled = (
    formValues: FormValues,
    isWithLiquid: boolean,
    formErrors: FormikErrors<FormValues>
  ) => {
    const setOfQuestions = isWithLiquid
      ? suitabilityQuestionswithLiquid
      : suitabilityQuestions;

    const currentQuestion = setOfQuestions[
      this.state.currentQuestionIndex
    ] as keyof FormValues;
    const questionValue = formValues[currentQuestion];
    // we only validate errors on the incomeLimitsRawValue since the rest are predetermined options

    return (
      (!questionValue && questionValue !== 0) ||
      (currentQuestion === "incomeLimitsRawValue" &&
        formErrors[currentQuestion])
    );
  };

  _onCancel = () => {
    this.setState({
      showCancelRolloverModal: true,
    });
    window.analytics.track("Cancelled Rollover Account Investment Profile");
  };

  _onModalClose = () => {
    this.setState({
      showTextModal: false,
    });
  };

  _displayPriorAnswer = (value: React.ReactNode, condition = true) => {
    if (
      (this.props.isUpdatingInvestmentProfile ||
        this.props.isInRequiredStepFlow) &&
      condition
    )
      return (
        <Form.Label className={"investment-form-label-currency"}>
          Your previous answer: {value}
        </Form.Label>
      );
  };

  displayRequiredUpdatesModal = () => {
    return (
      <div>
        <IconHeader variant="cardEmptyState" headerText="Accounts to update:" />

        {map(this.props.requiredUpdates, (update: RequiredUpdate) => {
          return (
            <div
              style={{
                display: "flex",
                paddingTop: "10px",
              }}
            >
              <Form.Label>
                {
                  accountTypesToEnglishMapping[
                    update.accountType as keyof typeof accountTypesToEnglishMapping
                  ]
                }
              </Form.Label>
            </div>
          );
        })}
      </div>
    );
  };
  render() {
    const {
      investmentProfile,
      suitability,
      currentInvestmentProfile,
      currentSuitability,
      loading,
    } = this.state;
    if (loading) {
      return <IconSpinner centered />;
    }

    const progPercent = this.props.isRolloverAccount ? "60" : "80";
    const formMetadata = get(currentInvestmentProfile, "formMetadata");
    const isFirstQuestion = this.state.currentQuestionIndex === 0;

    const requiredUpdatesTitle = "Required Investment Profile Update";

    const requiredUpdatesSubtext = this.props.multiAccountRequiredUpdates
      ? "Please update your investment profile for each of your IRA accounts. This helps us better manage your goals. Once updated, you can return to your dashboard."
      : "Every so often we'll ask you to update your investment profile and portfolio to help satisfy your investment goals. After you finish, you can return to your dashboard to invest!";

    const format = (value: number) => value;
    const title = this.props.accountType
      ? `YOUR ${
          accountTypesToEnglishMappingCAPS[
            this.props
              .accountType as keyof typeof accountTypesToEnglishMappingCAPS
          ]
        } INVESTMENT PROFILE`
      : "YOUR INVESTMENT PROFILE";

    const cardOne: InfoCard = {
      sections: [
        {
          key: 1,
          title: "Why We’re Asking",
          content: (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <p style={{ fontSize: 14 }}>
                  We’ll use this information to recommend a portfolio tailored
                  to your needs
                </p>
              </div>
            </>
          ),
        },
      ],
    };

    const cardTwo: InfoCard = {
      sections: [
        {
          key: 1,
          title: "We handle the investing for you.",
          content: (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start ",
                  flexDirection: "column",
                }}
              >
                <p style={{ fontSize: 14 }}>
                  Your savings will be invested in a portfolio tailored to your
                  needs.
                </p>
                <p style={{ fontSize: 14 }}>
                  We take care of all the re-balancing and busy work to help you
                  grow your wealth.
                </p>
                <p style={{ fontSize: 14 }}>
                  Icon is an SEC registered investment advisor.
                </p>
              </div>
            </>
          ),
        },
      ],
    };

    return (
      <div className="investment-profile">
        <SimpleModal
          show={this.state.showTextModal}
          onClose={this._onModalClose}
          title={requiredUpdatesTitle}
          subtext={requiredUpdatesSubtext}
          buttonText="Continue"
        >
          <p>{this.displayRequiredUpdatesModal()}</p>
        </SimpleModal>
        {this.state.showCancelRolloverModal && (
          <CancelRolloverModal
            show={this.state.showCancelRolloverModal}
            onClose={() =>
              this.setState({
                showCancelRolloverModal: false,
              })
            }
            onSuccess={() =>
              this.setState({
                showCancelRolloverModal: false,
              })
            }
          />
        )}
        {!(
          this.props.isUpdatingInvestmentProfile ||
          this.props.isInRequiredStepFlow
        ) && (
          <ProgressBar
            isEmployer={false}
            activeStepId={INVESTMENT_PROFILE.id}
            progressPercent={progPercent}
          />
        )}
        <Formik<FormValues>
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={schema}
          onSubmit={(values) => {
            this.submitInvestmentProfile(values);
          }}
          enableReinitialize={true}
          initialValues={{
            ...investmentProfile,
            ...suitability,
            incomeLimitsRawValue: null,
            wealthLimitsSelectedIndex: 0,
            liquidNetWorthLimitsSelectedIndex: 0,
            incomeLimitsDisplayValue: "",
          }}
        >
          {({
            handleSubmit,
            handleBlur,
            setFieldValue,
            touched,
            values,
            errors,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="55"
                >
                  <section className="page-title-wrap">
                    <article className="progress-cell">
                      <ul className="circular-progress-wrap">
                        <CircularProgressBar
                          strokeWidth="8"
                          sqSize="75"
                          percentage="55"
                        />
                      </ul>
                    </article>
                  </section>
                  <Carousel
                    className="suitability-carousel"
                    interval={null}
                    controls={false}
                    indicators={false}
                    touch={false} // we don't want the questions to change on swipe left/right
                    activeIndex={this.state.currentQuestionIndex}
                    onSelect={this._setSelectedQuestion}
                  >
                    <Carousel.Item>
                      <Row
                        className="mb-3"
                        id="questions"
                        style={{ justifyContent: "space-between" }}
                      >
                        <Form.Group
                          as={Col}
                          md={8}
                          lg={7}
                          controlId="formBasicAnnualIncomeField"
                        >
                          {isFirstQuestion && (
                            <InvestmentProfileQuestions
                              title={title}
                              investmentProfileQuestion="We help you manage your retirement savings."
                              subtext="We’ll use your answers to match you with a professionally managed portfolio—no fund picking required."
                            />
                          )}

                          <IconHeader
                            variant="selectBoxHeader"
                            headerText="What is your expected pre-tax income this year?"
                          />

                          <IconSubheader
                            variant="subheading"
                            subheader="If you’re not sure, just make your best estimate."
                          />

                          {this._displayPriorAnswer(
                            currentInvestmentProfile.annualIncome
                          )}
                          <Form.Control
                            style={{ width: 250, marginTop: 20 }}
                            name="incomeLimitsRawValue"
                            size="lg"
                            placeholder="$1000"
                            value={values.incomeLimitsDisplayValue}
                            onChange={(e) => {
                              /* We want input to display user friendly amt ($1,000,000) so we need to track
                                the raw numeric value and the display value as two separate entities to ensure formatCurrency 
                                works its magic and we are able to store the raw value that will be represented as INT vs string.
                              */
                              const rawValueFromInputString = +replace(
                                e.target.value,
                                /\D/g,
                                ""
                              );
                              void setFieldValue(
                                "incomeLimitsDisplayValue",
                                formatCurrency(rawValueFromInputString, 0)
                              );
                              void setFieldValue(
                                "incomeLimitsRawValue",
                                rawValueFromInputString
                              );
                            }}
                            isInvalid={
                              touched.incomeLimitsRawValue &&
                              !!errors.incomeLimitsRawValue
                            }
                            isValid={
                              touched.incomeLimitsRawValue &&
                              !errors.incomeLimitsRawValue
                            }
                            onBlur={handleBlur}
                          />

                          <Form.Control.Feedback type="invalid">
                            {errors.incomeLimitsRawValue}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          md={3}
                          className="paychex-info-card"
                        >
                          <IconInfoCard
                            title={cardTwo.title}
                            sections={
                              cardTwo.sections as unknown as React.ComponentProps<
                                typeof IconInfoCard
                              >["sections"]
                            }
                          />
                        </Form.Group>
                      </Row>
                    </Carousel.Item>
                    <Carousel.Item>
                      <Row
                        className="mb-3"
                        id="questions"
                        style={{ justifyContent: "space-between" }}
                      >
                        <Form.Group
                          as={Col}
                          md={7}
                          lg={7}
                          controlId="formBasicTotalNetWorth"
                        >
                          <InvestmentProfileQuestions
                            title={title}
                            investmentProfileQuestion="Please estimate the value of your cash, investments, and retirement accounts."
                            subtext="If you’re not sure, just make your best estimate."
                          />
                          <Form.Label className="investment-form-label-currency">
                            <div className="value">
                              <p className="currancy-label">
                                {
                                  wealthLimits[values.wealthLimitsSelectedIndex]
                                    .label
                                }
                              </p>
                            </div>
                          </Form.Label>
                          {this._displayPriorAnswer(
                            currentInvestmentProfile.totalNetWorth
                          )}
                          <div className="slider-horizontal">
                            <Slider
                              type="range"
                              min={0}
                              max={wealthLimits.length - 1}
                              tooltip={false}
                              value={values.wealthLimitsSelectedIndex}
                              format={format}
                              labels={{
                                0: "|",
                                1: "|",
                                2: "|",
                                3: "|",
                                4: "|",
                                5: "|",
                                6: "|",
                              }}
                              onChange={(value: number) => {
                                void setFieldValue(
                                  "wealthLimitsSelectedIndex",
                                  value
                                );
                              }}
                            />
                          </div>
                          <div className="slider-labels">
                            <p className="description">$50k or Lower</p>{" "}
                            <p className="description">Over $1M</p>
                          </div>
                          <Form.Control.Feedback type="invalid">
                            {errors.totalNetWorth}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          md={3}
                          className="paychex-info-card"
                        >
                          <IconInfoCard
                            title={cardOne.title}
                            sections={
                              cardOne.sections as unknown as React.ComponentProps<
                                typeof IconInfoCard
                              >["sections"]
                            }
                          />
                        </Form.Group>
                      </Row>
                    </Carousel.Item>
                    {!hideLiquidInvestment(
                      wealthLimits[values.wealthLimitsSelectedIndex].label
                    ) && (
                      <Carousel.Item>
                        <Row
                          className="mb-3"
                          id="questions"
                          style={{ justifyContent: "space-between" }}
                        >
                          <Form.Group
                            as={Col}
                            md={6}
                            lg={7}
                            controlId="formBasicLiquidNetWorth"
                          >
                            <InvestmentProfileQuestions
                              title={title}
                              investmentProfileQuestion="How much of your cash holdings are being held in a checking or savings account?"
                              subtext="If you’re not sure, just make your best estimate."
                            />
                            <Form.Label className="investment-form-label-currency">
                              <div className="value">
                                <p className="currancy-label">
                                  {
                                    wealthLimits[
                                      values.liquidNetWorthLimitsSelectedIndex
                                    ].label
                                  }
                                </p>
                              </div>
                              {this._displayPriorAnswer(
                                currentInvestmentProfile.liquidNetWorth,
                                !isNil(currentInvestmentProfile.liquidNetWorth)
                              )}
                            </Form.Label>
                            <div className="slider-horizontal">
                              <Slider
                                type="range"
                                min={0}
                                max={wealthLimits.length - 1}
                                tooltip={false}
                                value={values.liquidNetWorthLimitsSelectedIndex}
                                format={format}
                                labels={{
                                  0: "|",
                                  1: "|",
                                  2: "|",
                                  3: "|",
                                  4: "|",
                                  5: "|",
                                  6: "|",
                                }}
                                onChange={(value: number) => {
                                  void setFieldValue(
                                    "liquidNetWorthLimitsSelectedIndex",
                                    value
                                  );
                                }}
                              />
                            </div>
                            <div className="slider-labels">
                              <p className="description">$50k or Lower</p>{" "}
                              <p className="description">Over $1M</p>
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.liquidNetWorth}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            md={3}
                            className="paychex-info-card"
                          >
                            <IconInfoCard
                              title={cardOne.title}
                              sections={
                                cardOne.sections as unknown as React.ComponentProps<
                                  typeof IconInfoCard
                                >["sections"]
                              }
                            />
                          </Form.Group>
                        </Row>
                      </Carousel.Item>
                    )}
                    <Carousel.Item>
                      <Row className="mb-3" id="questions">
                        <Form.Group
                          as={Col}
                          md={8}
                          lg={7}
                          controlId="formBasicRiskTolerance"
                        >
                          <InvestmentProfileQuestions
                            title={title}
                            investmentProfileQuestion="When you think about how your money is invested, which one of the following statements describes your investment goal?"
                          />
                          {this._displayPriorAnswer(
                            !isNil(formMetadata) &&
                              get(
                                JSON.parse(formMetadata as string),
                                "riskToleranceObjectSelection.description"
                              )
                          )}
                          {riskToleranceValues.map((item) => (
                            <SelectBox
                              description={item.description}
                              key={item.key}
                              // SelectBox does not declare onBlur; the original
                              // passed it as an inert extra prop (React ignores
                              // unknown props), preserved here.
                              // @ts-expect-error inert extra prop on SelectBox
                              onBlur={handleBlur}
                              onSelect={() => {
                                void setFieldValue("riskTolerance", item.value);
                              }}
                              isSelected={values.riskTolerance === item.value}
                            />
                          ))}
                          <Form.Control.Feedback type="invalid">
                            {errors.riskTolerance}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>
                    </Carousel.Item>
                    <Carousel.Item>
                      <Row className="mb-3" id="questions">
                        <Form.Group
                          as={Col}
                          md={8}
                          lg={7}
                          controlId="formBasicExperience"
                        >
                          <InvestmentProfileQuestions
                            title={title}
                            investmentProfileQuestion="What is your current level of investment experience?"
                          />
                          {this._displayPriorAnswer(
                            get(
                              experienceValues.filter(
                                (v) =>
                                  v.value ===
                                  currentInvestmentProfile.experience
                              ),
                              "[0].description"
                            )
                          )}
                          {experienceValues.map((item) => (
                            <SelectBox
                              description={item.description}
                              key={item.key}
                              // SelectBox does not declare onBlur; the original
                              // passed it as an inert extra prop (React ignores
                              // unknown props), preserved here.
                              // @ts-expect-error inert extra prop on SelectBox
                              onBlur={handleBlur}
                              onSelect={() => {
                                void setFieldValue("experience", item.value);
                              }}
                              isSelected={values.experience === item.value}
                            />
                          ))}
                          <Form.Control.Feedback type="invalid">
                            {errors.experience}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>
                    </Carousel.Item>
                    <Carousel.Item>
                      <Row className="mb-3" id="questions">
                        <Form.Group
                          as={Col}
                          md={8}
                          lg={7}
                          controlId="formBasictimeHorizonLiquidity"
                        >
                          <InvestmentProfileQuestions
                            title={title}
                            investmentProfileQuestion="When do you anticipate needing the money in your retirement account?"
                          />
                          {this._displayPriorAnswer(
                            get(
                              {
                                ...oldTimeHorizonLiquidityValues,
                                ...timeHorizonLiquidityValues,
                              },
                              `${String(
                                currentSuitability.timeHorizonLiquidity
                              )}.description`
                            )
                          )}
                          {map(timeHorizonLiquidityValues, (value) => (
                            <SelectBox
                              description={value.description}
                              key={value.key}
                              // SelectBox does not declare onBlur; the original
                              // passed it as an inert extra prop (React ignores
                              // unknown props), preserved here.
                              // @ts-expect-error inert extra prop on SelectBox
                              onBlur={handleBlur}
                              onSelect={() => {
                                void setFieldValue(
                                  "timeHorizonLiquidity",
                                  value.value
                                );
                              }}
                              isSelected={
                                values.timeHorizonLiquidity === value.value
                              }
                            />
                          ))}
                          <Form.Control.Feedback type="invalid">
                            {errors.timeHorizonLiquidity}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>
                    </Carousel.Item>
                  </Carousel>
                  <div id="form-employer-company-profile">
                    <article className="col-form">
                      <div className="profile-buttons">
                        {this.props.error && (
                          <Alert type="error" msg={this.props.error} />
                        )}
                        <div className="submit-row btn-row">
                          {this.props.isRolloverAccount && (
                            <Button
                              type="button"
                              btnLabel={"Cancel"}
                              color={"cancel"}
                              name="cancel"
                              onClick={this._onCancel}
                            />
                          )}
                          {(() => {
                            const isLiquidInvestmentShown =
                              !hideLiquidInvestment(
                                wealthLimits[values.wealthLimitsSelectedIndex]
                                  .label
                              );
                            const isLastQuestion = this._isLastQuestion(
                              isLiquidInvestmentShown
                            );

                            const buttonProps = {
                              // the helper returns a truthy/falsy union; the raw
                              // value is forwarded unchanged (React coerces the
                              // DOM `disabled` attribute), narrowed for the typed
                              // Button prop.
                              disabled: this.calcContinueBtnDisabled(
                                values,
                                isLiquidInvestmentShown,
                                errors
                              ) as boolean | undefined,
                              name: "submit" as const,
                              btnLabel: "Continue",
                            };

                            return (
                              <Button
                                {...buttonProps}
                                withArrow={isLastQuestion}
                                type={isLastQuestion ? "submit" : "button"}
                                onClick={
                                  isLastQuestion ? noop : this._advanceCarousel
                                }
                                loading={
                                  isLastQuestion ? this.state.submitting : false
                                }
                              />
                            );
                          })()}
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualInvestmentProfile));
